<script setup lang="ts">
import {computed, ref} from 'vue';
import {useQuasar, type QPopupProxy} from 'quasar';
import Translator from '@/utils/common/Translator';
import WizardSlotSelector from './WizardSlotSelector.vue';
import {getMappingTypeShape} from './mappingTypeShape';
import BIWidgetRuntimeSelectorInfo = namaTypes.BIWidgetRuntimeSelectorInfo;
import BIWidgetSlotOverride = namaTypes.BIWidgetSlotOverride;

const props = defineProps<{
  widgetId: string;
  info?: BIWidgetRuntimeSelectorInfo | null;
}>();

const emit = defineEmits<{
  (e: 'applied', override: BIWidgetSlotOverride): void;
  (e: 'reset'): void;
}>();

const $q = useQuasar();
const popupRef = ref<QPopupProxy | null>(null);

function closePopup() {
  popupRef.value?.hide();
}

// Visible only when the mapping type has a v1 shape AND at least one selection type is enabled.
const isVisible = computed(() => {
  const info = props.info;
  if (!info || !info.wizardDataSourceId) return false;
  const shape = getMappingTypeShape(info.mappingType);
  if (!shape) return false;
  const hasDim = shape.slots.some(s => s.kind === 'dimension');
  const hasMeasure = shape.slots.some(s => s.kind === 'measure') || shape.supportsMultiMeasureSeries;
  const dimEnabled = hasDim && !info.disableRuntimeDimensionSelection;
  const measureEnabled = hasMeasure && !info.disableRuntimeMeasureSelection;
  return dimEnabled || measureEnabled;
});
</script>

<template>
  <q-btn v-if="isVisible" flat dense round size="sm" icon="tune"
         class="bi-runtime-slot-btn">
    <q-tooltip>{{ Translator.translate('biRuntimeSlotSelection') }}</q-tooltip>
    <q-popup-proxy ref="popupRef" :breakpoint="0">
      <q-card :dark="$q.dark.isActive">
        <WizardSlotSelector :widget-id="widgetId" :info="info!"
                            @applied="(o: BIWidgetSlotOverride) => emit('applied', o)"
                            @reset="emit('reset')"
                            @close="closePopup"/>
      </q-card>
    </q-popup-proxy>
  </q-btn>
</template>
